import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { useTheme } from "@mui/material/styles";
import { type FC, type MouseEvent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ValueChoiceGrid, type ValueChoiceItem } from "@/ui/Clickable/ValueChoiceGrid";
import { ShiftWindowIcon } from "@/ui/Icons/ShiftWindowIcon";
import type { ShiftAvailabilityWindow } from "@/utils/shiftAvailability";
import { getShiftIndexForWindow } from "@/utils/shiftOptionWindows";
import { CUSTOM_CARD_KEY, formatShiftHours, roundHours, type ShiftCardOption } from "./addStaffUtils";

/** Five cards a row on desktop (the design's four presets + Custom), three on a tablet, two on a phone. */
const MAX_COLUMNS = { xs: 2, sm: 3, md: 5 } as const;

const columnsFor = (count: number) => ({ xs: Math.min(count, MAX_COLUMNS.xs), sm: Math.min(count, MAX_COLUMNS.sm), md: Math.min(count, MAX_COLUMNS.md) });

type ShiftCardsProps = {
	options: ShiftCardOption[];
	selectedKey: string | null;
	/** The picked window — shown on the Custom card when it is the one picked. */
	window: ShiftAvailabilityWindow | null;
	onPickPreset: (window: ShiftAvailabilityWindow) => void;
	/** The Custom card was clicked; the tile is the anchor for the hours popover. */
	onCustomClick: (anchor: HTMLElement) => void;
	/** "Clear" on the Custom card: drop its hours, back to no shift picked. */
	onClearCustom: () => void;
	density: ShiftCardsDensity;
};

/** `inline` on a short screen: one-line cards, so the staff list below keeps its rows. */
export type ShiftCardsDensity = "comfortable" | "inline";

/**
 * Step 1's shift cards: one outline card per configured full shift (length as the eyebrow, sun/moon +
 * hours as the figure) and a dashed "Custom · Set hours" card that opens the time picker. Picking a card
 * IS the answer — step 2 re-evaluates immediately. Custom hours carry a "Clear" that undoes the pick.
 */
export const ShiftCards: FC<ShiftCardsProps> = ({ options, selectedKey, window, onPickPreset, onCustomClick, onClearCustom, density }) => {
	const { t } = useTranslation();
	const theme = useTheme();

	const items = useMemo<ValueChoiceItem[]>(() => {
		const presets = options.map(
			(option, index): ValueChoiceItem => ({
				value: option.key,
				eyebrow: t("add.staff.shift.length", { hours: roundHours(option.window.lengthHours) }),
				label: formatShiftHours(option.window),
				icon: <ShiftWindowIcon shiftIndex={option.shiftIndex} />,
				dataTestId: `add-nurse-shift-card-${index}`,
			}),
		);
		const customPicked = selectedKey === CUSTOM_CARD_KEY && !!window;
		const custom: ValueChoiceItem = {
			value: CUSTOM_CARD_KEY,
			eyebrow: t("add.staff.custom"),
			label: customPicked ? formatShiftHours(window) : t("add.staff.set.hours"),
			icon: customPicked ? (
				<ShiftWindowIcon shiftIndex={getShiftIndexForWindow(window)} />
			) : (
				<ScheduleOutlinedIcon fontSize="inherit" sx={{ color: theme.palette.primary.main }} />
			),
			dashed: true,
			dataTestId: "add-nurse-edit-time-button",
			action: customPicked
				? {
						label: t("add.staff.custom.clear"),
						ariaLabel: t("add.staff.custom.clear.aria"),
						icon: <CloseRoundedIcon fontSize="inherit" />,
						onClick: onClearCustom,
						dataTestId: "add-nurse-custom-clear-button",
					}
				: undefined,
		};
		return [...presets, custom];
	}, [options, selectedKey, window, t, theme.palette.primary.main, onClearCustom]);

	const handleSelect = (value: number | string, event: MouseEvent<HTMLElement>) => {
		if (value === CUSTOM_CARD_KEY) {
			onCustomClick(event.currentTarget);
			return;
		}
		const option = options.find((candidate) => candidate.key === value);
		if (option) onPickPreset(option.window);
	};

	return (
		<ValueChoiceGrid
			items={items}
			selected={selectedKey}
			onSelect={handleSelect}
			tileVariant="outline"
			density={density}
			columns={columnsFor(items.length)}
			dataTestId="add-nurse-shift-cards"
		/>
	);
};

type LockedShiftSummaryProps = { window: ShiftAvailabilityWindow; density: ShiftCardsDensity };

/**
 * A request's shift, which this panel may not change: the same card, picked, with where the hours came
 * from. It stays a card (not plain text) so a locked and an open panel read as the same step.
 */
export const LockedShiftSummary: FC<LockedShiftSummaryProps> = ({ window, density }) => {
	const { t } = useTranslation();
	const lengthHours = roundHours(window.end.diff(window.start, "hours").hours);
	const item: ValueChoiceItem = {
		value: "locked",
		eyebrow: t("add.staff.shift.length", { hours: lengthHours }),
		label: formatShiftHours(window),
		caption: t("add.staff.locked.caption"),
		icon: <ShiftWindowIcon shiftIndex={getShiftIndexForWindow(window)} />,
		dataTestId: "add-nurse-locked-shift",
	};
	return (
		<ValueChoiceGrid
			items={[item]}
			selected={item.value}
			// Nothing to change: the one card is the request's shift.
			onSelect={() => undefined}
			tileVariant="outline"
			density={density}
			columns={{ xs: MAX_COLUMNS.xs, sm: MAX_COLUMNS.sm, md: MAX_COLUMNS.md }}
			dataTestId="add-nurse-shift-cards"
		/>
	);
};
